import * as yup from 'yup'
import { addHours, isAfter, isBefore, isSameDay } from 'date-fns'
import { SlotStatus } from '../../../models/DistributionPlan'

export const distributionFormSchema = yup.object().shape({
  termId: yup.string().required('Term is required'),
  templateId: yup.string().required('Survey is required'),

  startTimestamp: yup
    .date()
    .required('Default Start Date is required')
    .max(yup.ref('endTimestamp'), 'Start Date must be before End Date'),

  endTimestamp: yup
    .date()
    .required('Default End Date is required')
    .test('future-date', 'End Date must be in the future', (value) => {
      if (!value) return true
      return isAfter(new Date(value), new Date())
    })
    .test('after-start', 'End Date must be after Start Date', function (value) {
      const { startTimestamp, slotStatus } = this.parent
      if (!value || !startTimestamp) return true
      // In Live state, we don't validate against start timestamp
      if (slotStatus === SlotStatus.Live) return true
      return isAfter(new Date(value), new Date(startTimestamp))
    })
    .test(
      'is-24-hours-later',
      'End Date must be at least 24 hours after Start Date',
      function (endTimestamp) {
        const { startTimestamp, slotStatus } = this.parent
        if (!startTimestamp || !endTimestamp || slotStatus === SlotStatus.Live) return true
        return isAfter(new Date(endTimestamp), addHours(new Date(startTimestamp), 24))
      }
    ),

  releaseTimestamp: yup
    .date()
    .transform((value, originalValue) => {
      if (originalValue === 'Rolling Release') {
        return new Date('3000-01-01T00:00:00Z')
      }
      const parsed = new Date(originalValue)
      return isNaN(parsed.getTime()) ? null : parsed
    })
    .test('future-date', 'Release Date must be in the future', (value) => {
      if (!value) return true
      return isAfter(new Date(value), new Date())
    })
    .test('after-end', 'Release Date must be on or after End Date', function (value) {
      const { endTimestamp, slotStatus } = this.parent
      if (!value || !endTimestamp) return true
      const releaseDate = new Date(value)
      const endDate = new Date(endTimestamp)
      return isAfter(releaseDate, endDate) || isSameDay(releaseDate, endDate)
    })
    .when(['isRollingResultsEnabled', 'slotStatus'], {
      is: (isRolling: boolean, status: string) => !isRolling && status !== SlotStatus.Closed,
      then: (schema) => schema.required('Release Date is required'),
      otherwise: (schema) => schema.strip()
    }),

  isAcademicLevelAddedQuestionsAllowed: yup.boolean().notRequired(),
  isInstructorAddedQuestionsAllowed: yup.boolean().notRequired(),
  isRollingResultsEnabled: yup.boolean().notRequired(),

  academicLevelAddedQuestions: yup
    .object({
      academicUnitCategories: yup.array().nullable(),
      questionCount: yup.number().nullable(),
      isCustomQuestionAllowed: yup.boolean().notRequired()
    })
    .test('academic-questions-validation', '', function (value) {
      const { isAcademicLevelAddedQuestionsAllowed } = this.parent
      if (!isAcademicLevelAddedQuestionsAllowed) return true

      if (!value?.academicUnitCategories || value.academicUnitCategories.length === 0) {
        return this.createError({
          path: 'academicLevelAddedQuestions.academicUnitCategories',
          message: 'At least one Academic Unit level is required'
        })
      }

      if (value.questionCount === undefined || value.questionCount === null || value.questionCount < 1) {
        return this.createError({
          path: 'academicLevelAddedQuestions.questionCount',
          message: 'Number of questions is required and must be at least 1'
        })
      }

      return true
    }),

  instructorAddedQuestions: yup
    .object({
      questionCount: yup.number().nullable(),
      isCustomQuestionAllowed: yup.boolean().notRequired()
    })
    .test('instructor-questions-validation', '', function (value) {
      const { isInstructorAddedQuestionsAllowed } = this.parent

      if (!isInstructorAddedQuestionsAllowed) return true

      if (!value) {
        return this.createError({
          path: 'instructorAddedQuestions.questionCount',
          message: 'Number of questions is required'
        })
      }

      if (typeof value.questionCount !== 'number' || value.questionCount < 1) {
        return this.createError({
          path: 'instructorAddedQuestions.questionCount',
          message: 'Number of questions must be at least 1'
        })
      }

      return true
    })
})
